/**
 * Maps a `view.component` name (from an `AgentUiAction` of type `"view"`) to
 * the React component that renders it. Three core views any backend module
 * can emit are registered here directly: `chart` (`AgentChartView`), `choice`
 * (`AgentChoiceView`) and `form` (`AgentFormView`). A domain package (P2–P5)
 * adds its own by creating `views/<domain>.tsx` exporting a `<domain>AgentViews:
 * Record<string, AgentViewComponent>` map and spreading it in here (design
 * D8): one import, one line.
 */
import type { FC } from "react";
import type { AgentUiAction } from "../agentStream.js";
import { AgentChartView } from "./AgentChartView.js";
import { AgentChoiceView } from "./AgentChoiceView.js";
import { AgentFormView } from "./AgentFormView.js";

export type AgentViewSpec = Extract<AgentUiAction, { type: "view" }>["view"];

export type AgentViewComponentProps = {
  view: AgentViewSpec;
  /** Sends a chat message on the user's behalf; `confirm` marks it as the answer "yes". */
  onSend: (text: string, options?: { confirm?: boolean }) => void;
  busy: boolean;
  /**
   * The following user message's text, once this view's own message is no
   * longer the newest one — i.e. the turn has already been answered.
   * `undefined` means "still the newest, interactive"; a defined value
   * (possibly `""`) means "render read-only", the answer to mark chosen
   * options against (only `choice` reads this; other views ignore it).
   */
  answeredWith?: string;
};

export type AgentViewComponent = FC<AgentViewComponentProps>;

export const agentViewRegistry: Record<string, AgentViewComponent> = {
  chart: AgentChartView,
  choice: AgentChoiceView,
  form: AgentFormView,
};

/**
 * The component registered for a view's `component` name, or `undefined`.
 * Own properties only: the name comes from stored data, and an inherited
 * name (`constructor`, `toString`, `__proto__`) must be "not registered",
 * never an `Object.prototype` member rendered as a component — the same rule
 * as the backend's `findAgentViewDataHandler`.
 */
export function agentViewComponentFor(
  component: string
): AgentViewComponent | undefined {
  return Object.hasOwn(agentViewRegistry, component)
    ? agentViewRegistry[component]
    : undefined;
}
